import { createContext, Key, useContext } from "react";
import {
  ISimpleTableColumnFilter,
  ISimpleTableField,
  ISimpleTableRow,
  ISimpleTableSort,
} from "./interface";

/**
 * Context for the SimpleTable component. This is used to pass data and functions down to the child components without having to prop drill.
 * It includes the table data, selection state, sorting state, and functions to update these states.
 * It also includes state for pagination and column filtering.
 * The context is created with default values, which are overridden by the SimpleTable component when it is used.
 * This allows the child components to access the context without having to worry about whether it has been provided or not.
 * The context is typed with the ISimpleTableContext interface, which defines the shape of the context data and functions.
 */
export interface ISimpleTableContext {
  /** Ths table id */
  id: string;
  /** The fields (columns) of the table */
  fields: ISimpleTableField[];
  /** The key field of the table */
  keyField: string;
  /** The data to be displayed in the table */
  viewData: ISimpleTableRow[];
  /** The total number of rows in the table */
  totalRows: number;
  /** Function to set the table data */
  setTableData: (ret: ISimpleTableRow[]) => void;
  /** Whether the table is selectable */
  selectable?: boolean;
  /** Whether to show the search input */
  showSearch?: boolean;
  /** Whether to show the filter input */
  showFilter?: boolean;
  /** The label for the filter input */
  filterLabel?: string;
  /** Whether the filter is active */
  filterData?: boolean;
  /** Function to set the filter state */
  setFilterData: (ret: boolean) => void;
  /** The label for the search input */
  searchLabel?: string;
  /** The current search text */
  searchText?: string;
  /** Function to set the search text */
  setSearchText: (ret: string) => void;
  /** The current sort state */
  sortBy?: ISimpleTableSort;
  /** Function to update the sort state */
  updateSortBy: (ret: ISimpleTableField) => void;
  /** The currently selected rows */
  currentSelection: Key[];
  /** Function to toggle the selection of all rows */
  toggleAllCurrentSelection: () => void;
  /** Function to toggle the selection of a single row */
  toggleSelection: (ret: Key) => void;

  /** The widths of the columns */
  columnWidths: { name: string; width: string }[];
  /** Function to set the width of a column */
  setColumnWidth: (columnName: string, width: string) => void;
  /** The number of rows per page */
  pageRows: number;
  /** Function to set the number of rows per page */
  setPageRows: (ret: number) => void;
  /** The index of the first row */
  firstRow: number;
  /** Function to set the index of the first row */
  setFirstRow: (ret: number) => void;

  /** All unique values in each column, sorted */
  allColumnItems: ISimpleTableColumnFilter[];
  /** The column filter items after other filters have been applied, sorted */
  currentColumnItems: ISimpleTableColumnFilter[];
  /** The currently active column filter */
  currentColumnFilter: number | null;
  /** Function to set the currently active column filter */
  setCurrentColumnFilter: (ret: number | null) => void;
  /** The currently applied column filters */
  currentColumnFilters: ISimpleTableColumnFilter[];
  /** Function to set the currently applied column filters */
  setCurrentColumnFilters: (ret: ISimpleTableColumnFilter[]) => void;

  /** The class name for the input group */
  inputGroupClassName?: string;
  /** The class name for the filter label */
  filterLabelClassName?: string;
  /** The class name for the filter checkbox */
  filterCheckClassName?: string;
  /** The class name for the search label */
  searchLabelClassName?: string;
  /** The class name for the search input */
  searchInputClassName?: string;

  /** The background color for the table header */
  headerBackgroundColor?: string;
}

// istanbul ignore next
const defaultFn = () => {};

export const defaultContext: ISimpleTableContext = {
  id: "simple-table",
  fields: [],
  keyField: "",
  viewData: [],
  totalRows: 0,
  setTableData: defaultFn,
  setFilterData: defaultFn,
  setSearchText: defaultFn,
  updateSortBy: defaultFn,
  currentSelection: [],
  toggleAllCurrentSelection: defaultFn,
  toggleSelection: defaultFn,
  setColumnWidth: defaultFn,
  pageRows: 50,
  setPageRows: defaultFn,
  firstRow: 0,
  setFirstRow: defaultFn,
  columnWidths: [],
  allColumnItems: [],
  currentColumnItems: [],
  currentColumnFilter: null,
  currentColumnFilters: [],
  setCurrentColumnFilter: defaultFn,
  setCurrentColumnFilters: defaultFn,
} as ISimpleTableContext;

export const SimpleTableContext = createContext<ISimpleTableContext>(defaultContext);

export const useSimpleTableContext = () => {
  const context = useContext(SimpleTableContext);
  return context;
};
